import type { CartItem } from "@/features/cart/types";
import type { DiscountType } from "@/features/cart/types/discount.type";
import { getDiet } from "@/features/diets/api/get-diet";
import { sendEvent } from "../gtm/event";
import type { CouponSourceType } from "../types/event.type";

const formatCouponValue = (discounts: DiscountType[], code: string): string => {
  const discount = discounts.find((item) => item.name === code);
  if (!discount) {
    return "";
  }

  if (discount.unit === "percentage" && discount.percentage != null) {
    return `${discount.percentage}%`;
  }

  return `${Math.round(Math.abs(discount.amount.gross))}PLN`;
};

const resolveItemBrand = async (
  items: CartItem[],
  code: string,
): Promise<string> => {
  const couponItems = items.filter((item) =>
    item.pricing.discounts?.some((discount) => discount.name === code),
  );
  const merchantIds = [
    ...new Set(
      couponItems
        .map((item) => item.configurationItem.merchantId)
        .filter(Boolean),
    ),
  ];
  const [first] = couponItems;
  if (merchantIds.length !== 1 || !first) {
    return "MULTIBRAND";
  }

  try {
    const diet = await getDiet(first.configurationItem.dietId);
    return diet?.catering?.name || "MULTIBRAND";
  } catch {
    return "MULTIBRAND";
  }
};

export const useEventCoupon = () => {
  const sendCouponAddedEvent = async ({
    coupon,
    cartId,
    status,
    source,
    link_text,
    items,
    discounts,
  }: {
    coupon: string;
    cartId?: string;
    status: "success" | "fail";
    source: CouponSourceType;
    link_text?: string;
    items: CartItem[];
    discounts: DiscountType[];
  }) => {
    const item_brand = await resolveItemBrand(items, coupon);
    sendEvent({
      event: "COUPON_ADDED",
      ecommerce: {
        cartId,
        coupon,
        status,
        ...(link_text ? { link_text } : {}),
        item_brand,
        coupon_value: formatCouponValue(discounts, coupon),
        source,
      },
    });
  };

  const sendCouponRemovedEvent = async ({
    coupon,
    cartId,
    items,
    discounts,
  }: {
    coupon: string;
    cartId?: string;
    items: CartItem[];
    discounts: DiscountType[];
  }) => {
    const item_brand = await resolveItemBrand(items, coupon);
    sendEvent({
      event: "COUPON_REMOVED",
      ecommerce: {
        cartId,
        coupon,
        item_brand,
        coupon_value: formatCouponValue(discounts, coupon),
      },
    });
  };

  return { sendCouponAddedEvent, sendCouponRemovedEvent };
};
